import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal } from '@angular/cdk/portal';
import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  NgZone,
  EventEmitter,
  Input,
  ChangeDetectorRef,
  Self,
  AfterContentInit
} from '@angular/core';
import {
  NgForm, NgControl, ValidatorFn, ValidationErrors, AbstractControl, ControlValueAccessor
} from '@angular/forms';
import { WebcamImage, WebcamInitError } from 'ngx-webcam';
import {
  BehaviorSubject, combineLatest, merge, Observable
} from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { IrisButtonTypes, IrisButtonIconPositions } from '@sondermind/iris/button';
import {
  IconColors,
  IconSizes,
  IconUsageTypes,
} from '@sondermind/iris/icon';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';

import { BreakpointsService } from '@sondermind/utilities/services';
import { ImageUploadService, Mode } from '../../services/image-upload.service';
import { SupportChecks } from '../../services/support-checks';

import { FormFieldComponent } from '../form-field/form-field.component';

let nextId = 0;

@Component({
  selector: 'sonder-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  standalone: false
})
export class ImageUploadComponent extends FormFieldComponent
  implements OnInit, AfterViewInit, AfterContentInit, ControlValueAccessor {
  buttonId = `add-image-${nextId += 1}`;
  inputId = `card-upload-${nextId += 1}`;
  _disabled = false;
  dropdownOpen: boolean = false;

  // used to get a uri to display in the backgound
  // we can't use the uploaded uri, since it's a write-only bucket
  dataUriSubj = new BehaviorSubject<string>(null);

  // the mode of the control
  // used to switch out displays for uploading, etc.
  modeSubj: BehaviorSubject<Mode> = new BehaviorSubject<Mode>(Mode.Pristine);

  irisButtonTypes = IrisButtonTypes;
  iconColors = IconColors;
  iconSizes = IconSizes;
  iconUsageTypes = IconUsageTypes;
  irisButtonIconPositions = IrisButtonIconPositions;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  constructor(
    @Self() private ngControl: NgControl,
    private imageUploadService: ImageUploadService,
    private overlay: Overlay,
    breakpoint: BreakpointsService,
    form: NgForm,
    cdr: ChangeDetectorRef,
    zone: NgZone
  ) {
    super(form, cdr, zone, breakpoint);

    // eslint-disable-next-line no-param-reassign
    ngControl.valueAccessor = this;
  }

  // image upload management stuff
  @Input() s3BucketName: string;

  // modal overlay stuff
  @ViewChild('camPortal')
    camPortal: CdkPortal;
  camOverlay: OverlayRef;
  camTrigger = new EventEmitter();

  camError$: Observable<boolean>;

  // support for checking if we're allowed to webcam
  supportChecks: SupportChecks;
  canWebcam$: Observable<boolean>;

  // used to get a uri to display in the backgound
  // we can't use the uploaded uri, since it's a write-only bucket
  dataUriBackground$: Observable<string>;

  // the mode of the control
  // used to switch out displays for uploading, etc.
  stateClasses$: Observable<{ [key: string]: boolean; }>;

  // control stuff
  @Input() override label: string = '';
  @Input() override required: boolean = false;
  @Input() placeholderUri: string;
  pathName: string;

  onChanged: (any) => void = (_: any) => {};
  onTouched: () => void = () => {};

  override ngOnInit() {
    this.supportChecks = new SupportChecks();
    this.camError$ = this.imageUploadService.camErrorSubj.asObservable();
    this.canWebcam$ = combineLatest(
      this.supportChecks.capture$,
      this.supportChecks.webcam$,
      this.camError$,
    ).pipe(
      // on mobile, the file picker provides camera access
      // so we disable our special camera handling
      map(([capture, webcam, error]) => !capture && webcam && !error),
    );

    // used to get a uri to display in the backgound
    // we can't use the uploaded uri, since it's a write-only bucket
    this.dataUriBackground$ = this.dataUriSubj.pipe(
      map((x) => x || null)
    );

    // the mode of the control
    // used to switch out displays for uploading, etc.
    this.stateClasses$ = combineLatest(
      this.dataUriSubj,
      this.modeSubj,
    ).pipe(
      map(([data, mode]) => ({
        '-select': mode === Mode.Pristine,
        '-camera': mode === Mode.Working,
        '-upload': mode === Mode.Uploading,
        '-dirty': data != null
      }))
    );

    this.ngControl.control.setValidators(this.getValidators());
    this.ngControl.control.updateValueAndValidity();
    this.supportChecks.initialize();

    if (this.placeholderUri) {
      this.dataUriSubj.next(this.placeholderUri);
    }
  }

  ngAfterViewInit(): void {
    this.camOverlay = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'sonder-overlay-backdrop-verydark',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    // Fix: Set the parent's control property without using super.control
    this.setParentControl(this.ngControl);
  }

  // Add a helper method to set the parent control
  private setParentControl(control: NgControl): void {
    Object.getPrototypeOf(this).constructor.prototype.control = control;
  }

  override ngAfterContentInit(): void {
    super.ngAfterContentInit();
  }

  // sonder-form-field overrides
  override set disabled(v: boolean) {
    // eslint-disable-next-line no-underscore-dangle
    this._disabled = v;
  }

  override get disabled(): boolean {
    // eslint-disable-next-line no-underscore-dangle
    return this._disabled;
  }

  validate(c: AbstractControl): ValidationErrors | null {
    if (this.modeSubj.value !== Mode.Pristine) {
      return null;
    }

    const errors: any = {};

    if (this.required && !this.imageUploadService.camErrorSubj.getValue()) {
      if (c.value == null || c.value === '') {
        errors.required = true;
      }
    }

    return errors;
  }

  // control value accessor

  writeValue(value: any): void {
    // we don't support an existing value (like input type=file)
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // eslint-disable-next-line consistent-return
  launchCam(): void {
    // return if field is disabled
    if (this.disabled) {
      return null;
    }

    this.imageUploadService.clearErrors();
    this.modeSubj.next(Mode.Working);
    this.onTouched();

    const destroy = new EventEmitter();
    const ref = this.camOverlay.attach(this.camPortal);

    // clean up when the view's gone
    ref.onDestroy(() => {
      this.modeSubj.next(Mode.Pristine);
      destroy.emit();
    });

    // escape should be close
    // everything else is trigger
    merge(
      this.camOverlay.keydownEvents(),
      this.camOverlay.backdropClick()
    ).pipe(
      takeUntil(destroy),
    ).subscribe((e) => {
      if (e instanceof KeyboardEvent && e.key === 'Escape') {
        destroy.next(null);
        this.camOverlay.detach();
      } else {
        this.camTrigger.emit();
        e.preventDefault();
      }
    });
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  deletePhoto(): void {
    this.dataUriSubj.next(null);
  }

  /** On mobile, programmatically click the file input */
  fileInputClick(fileInput: HTMLInputElement): void {
    fileInput.click();
    this.fileInputClicked();
  }

  fileInputClicked(): void {
    this.imageUploadService.clearErrors();
    this.onTouched();
  }

  selectedFile(e: Event): void {
    this.onTouched();

    const input: HTMLInputElement = e.target as HTMLInputElement;
    if (input.files.length) {
      const file = input.files.item(0);
      this.modeSubj.next(Mode.Uploading);
      this.resizeImage(file).subscribe((resizedImage: Blob) => {
        this.imageUploadService.uploadImage(resizedImage)
          .subscribe({
            next: (downloadFrom) => {
              this.modeSubj.next(Mode.Pristine);
              this.pathName = downloadFrom;
              this.onChanged(this.pathName);
            },
            error: (error) => {
              this.modeSubj.next(Mode.Pristine);
              this.dataUriSubj.next(null);
              this.imageUploadService.errors.push('Error while uploading, try again later.');

              // rethrow, we still want logging on this
              throw error;
            }
          });
      });
    }
  }

  resizeImage(file: File): Observable<Blob> {
    return new Observable((observer) => {
      const width = 640;
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.src = (event.target as any).result;
        img.onload = () => {
          const elemCanvas = document.createElement('canvas');
          const scaleFactor = width / img.width;
          elemCanvas.width = width;
          elemCanvas.height = img.height * scaleFactor;

          const ctx = elemCanvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, img.height * scaleFactor);

          const uri = ctx.canvas.toDataURL('image/jpeg');
          const blob = this.imageUploadService.generateBlobFromUri(uri);
          observer.next(blob);
        };

        this.dataUriSubj.next(reader.result as string);
      };

      reader.readAsDataURL(file);
    });
  }

  capturedImage(img: WebcamImage): void {
    const dataUri = img.imageAsDataUrl;

    this.dataUriSubj.next(dataUri);
    this.camOverlay.detach();

    setTimeout(() => {
      // todo: change webcam component to return blob
      const blob = this.imageUploadService.generateBlobFromUri(dataUri);
      this.imageUploadService.uploadImage(blob)
        .subscribe({
          next: (downloadFrom) => {
            this.modeSubj.next(Mode.Pristine);
            this.pathName = downloadFrom;
            this.onChanged(this.pathName);
          },
          error: (error) => {
            this.modeSubj.next(Mode.Pristine);
            this.dataUriSubj.next(null);
            this.imageUploadService.errors.push('Error while uploading, try again later.');

            // rethrow, we still want logging on this
            throw error;
          }
        });
    });
  }

  handleCamError(err: WebcamInitError): void {
    this.camOverlay.detach();
    this.imageUploadService.handleCamError(err);
  }

  /**
   * Returns an array of validator functions, which always includes the validate method
   * from this class and any other pre-existing validators from the control.
   *
   * @private
   * @return {*}  {ValidatorFn[]}
   * @memberof ImageUploadComponent
   */
  private getValidators(): ValidatorFn[] {
    const validators = [this.validate.bind(this)];

    // eslint-disable-next-line no-extra-boolean-cast
    if (!!this.ngControl.control.validator) {
      return [this.ngControl.control.validator, ...validators];
    }

    return validators;
  }
}
